import { Fragment, useCallback, useState } from 'react';
import { defineMessages, useIntl } from '@atlas/text';
import Form from '@atlas/uicorn/lib/Control/Form';
import { ReactEditor, useSlate } from 'slate-react';
import window from '@atlas/tool/box/browser/window';
import isUrl from '@atlas/tool/box/type/isUrl';
import IntegrationIcon from '@atlas/uicorn/lib/Icon/IntegrationIcon';
import Modal from '@atlas/uicorn/lib/Display/Modal';
import Button from '@atlas/uicorn/lib/Control/Button';
import FormItem from '@atlas/uicorn/lib/Control/FormItem';
import Input from '@atlas/uicorn/lib/Input/Input';
import type { EmbedBlock, SlateBlock } from '@atlas/renderer';
import { Editor, Node, Path, Transforms } from 'slate';
import BlockButton from '@atlas/uicorn/lib/RichEditor/block-button';
import type { TFormItemRuleObject } from '@atlas/uicorn/lib/Control/FormItem';

export const embedButtonMessages = defineMessages({
    addEmbed: { id: 'actAddEmbed', defaultMessage: 'Add embed' },
    addEmbedToArticle: { id: 'lblAddEmbedToArticle', defaultMessage: 'Add embed to article' },
    cancel: { id: 'actCancel', defaultMessage: 'Cancel' },
    add: { id: 'actAdd', defaultMessage: 'Add' },
    requiredField: { id: 'msgRequiredField', defaultMessage: 'Required field' },
    invalidEmbedUrl: { id: 'msgInvalidEmbedUrl', defaultMessage: 'Invalid embed URL' },
});

export default function EmbedButton() {
    const [isVisible, setIsVisible] = useState(false);
    const [form] = Form.useForm();
    const editor = useSlate();
    const intl = useIntl();

    const validator = useCallback(
        (_: TFormItemRuleObject, value: string) =>
            new Promise<void>((resolve, reject) => {
                if (!value) return resolve();
                if (isUrl.ofSite(value)) return resolve();
                if (value.startsWith('<')) return resolve();
                reject();
            }),
        [editor],
    );

    return (
        <Fragment>
            <BlockButton
                block="embed"
                icon={<IntegrationIcon />}
                title={intl.formatMessage(embedButtonMessages.addEmbed)}
                action={() => setIsVisible((f) => !f)}
            />
            {isVisible && (
                <Modal
                    header={intl.formatMessage(embedButtonMessages.addEmbedToArticle)}
                    onClose={() => setIsVisible(false)}
                    footer={
                        <>
                            <Button
                                type="tertiary"
                                onClick={() => setIsVisible(false)}
                                children={intl.formatMessage(embedButtonMessages.cancel)}
                            />
                            <Button
                                type="primary"
                                onClick={form.submit}
                                children={intl.formatMessage(embedButtonMessages.add)}
                            />
                        </>
                    }
                >
                    <Form
                        form={form}
                        onFinish={({ source }) => {
                            setIsVisible(false);
                            insertEmbedFromLink(editor, source);
                        }}
                        preserve={false}
                        focusFirstInput
                    >
                        <FormItem
                            name="source"
                            rules={[
                                {
                                    required: true,
                                    message: intl.formatMessage(embedButtonMessages.requiredField),
                                },
                                { validator, message: intl.formatMessage(embedButtonMessages.invalidEmbedUrl) },
                            ]}
                        >
                            <Input placeholder="https://... or <html>code..." autoFocus />
                        </FormItem>
                    </Form>
                </Modal>
            )}
        </Fragment>
    );
}

const createEmbedNodeFromSource = (source: string): EmbedBlock | undefined => {
    // Normalizes HTML (closing tags) and doesn't affect links
    const normalized = window ? Object.assign(window.document.createElement('i'), { innerHTML: source }).innerHTML : '';
    return {
        type: 'embed',
        source: normalized,
        height: 360,
        children: [{ text: '' }],
    };
};

const insertEmbedFromLink = (editor: Editor, source: string) => {
    const node = createEmbedNodeFromSource(source);
    if (!node) return false;

    ReactEditor.focus(editor);
    const { selection } = editor;

    if (!selection) {
        // Insert the new embed node at the bottom of the Editor when selection is falsy
        Transforms.insertNodes(editor, node, { select: true });
        return true;
    }

    const [parentNode, parentPath] = Editor.parent(editor, selection.focus?.path);

    if (editor.isVoid(parentNode as SlateBlock) || Node.string(parentNode).length) {
        // Insert the new embed node after the void node or a node with content
        Transforms.insertNodes(editor, node, {
            at: Path.next(parentPath),
            select: true,
        });
    } else {
        // If the node is empty, replace it instead
        Transforms.removeNodes(editor, { at: parentPath });
        Transforms.insertNodes(editor, node, {
            at: parentPath,
            select: true,
        });
    }
    Transforms.unwrapNodes(editor, { mode: 'all', split: true });

    return true;
};
